import type { InputProps } from '@ixigo/iui/Input';
import { useControlledState, useEventCallback } from '@ixigo/iui/utils';
import { type UseAsyncListOptions, useAsyncList } from '@repo/shared/hooks';
import { useRef } from 'react';

export interface UseComboboxParams<Value, Meta = unknown> {
	load: UseAsyncListOptions<Value, Meta>['load'];
	loadOptions?: Omit<UseAsyncListOptions<Value, Meta>, 'load'>;

	value?: Value; // controlled selected item
	defaultValue?: Value; // uncontrolled initial value
	onChange?: ({ value }: { value: Value }) => void;

	itemToString: (item: Value) => string;
	clearOnFocus?: boolean;
}
export function useCombobox<Value, Meta = unknown>(
	params: UseComboboxParams<Value, Meta>,
) {
	const {
		value: valueProp,
		defaultValue,
		itemToString,
		onChange,
		clearOnFocus = true,
	} = params;
	const {
		items,
		meta,
		loading,
		query: inputValue,
		setQuery,
	} = useAsyncList<Value, Meta>({
		...params.loadOptions,
		load: params.load,
	});

	const getItemValue = (item: Value) => {
		return item == null ? '' : (itemToString?.(item) ?? item.toString());
	};

	const inputRef = useRef<HTMLInputElement>(null);

	const [value, setValue] = useControlledState({
		controlled: valueProp,
		default: defaultValue,
	});

	const effectiveValue = inputValue ?? getItemValue(value);

	function getInputProps(
		extraProps: React.ComponentPropsWithoutRef<'input'>,
	): InputProps {
		return {
			value: effectiveValue,
			...extraProps,
			inputRef,
			onChange: (e) => {
				setQuery(e.target.value);
				extraProps.onChange?.(e);
			},
			onBlur: (e) => {
				extraProps.onBlur?.(e);
				setQuery();
			},
			onFocus: (e) => {
				extraProps.onFocus?.(e);
				if (clearOnFocus) {
					setQuery('');
				}
			},
		};
	}
	function getClearProps() {
		return {
			value: effectiveValue,
			onClick: () => {
				setQuery('');
				inputRef.current?.focus();
			},
		};
	}

	const selectValue = useEventCallback(({ item }: { item: Value }) => {
		setValue(item);
		onChange?.({ value: item });
	});
	function getItemProps(itemProps: { item: Value }) {
		return {
			onClick: () => selectValue(itemProps),
		};
	}

	function getListProps() {
		return {
			role: 'listbox',
		};
	}

	return {
		getInputProps,
		getClearProps,
		getItemProps,
		getListProps,
		items,
		meta,
		loading,
		query: inputValue,
		value,
		inputRef,
		selectValue,
	};
}
